import React from 'react';

interface AudioVisualizerProps {
  isPlaying: boolean;
  className?: string;
}

const AudioVisualizer: React.FC<AudioVisualizerProps> = ({ isPlaying, className = '' }) => {
  return (
    <div className={`relative ${className}`}>
      {/* Visualización de ondas estilo videojuegos */}
      <div className="flex justify-center items-center h-32 rounded-lg p-4 relative overflow-hidden" style={{
        background: 'linear-gradient(135deg, #0a0a0f, #1a1a2e, #16213e, #0f3460, #0a0a0f)',
        border: '2px solid #00ff88',
        boxShadow: '0 0 30px rgba(0, 255, 136, 0.4), inset 0 0 30px rgba(0, 255, 136, 0.1), 0 0 60px rgba(0, 255, 255, 0.2)'
      }}>
        {/* Grid de fondo estilo consola retro */}
        <div className="absolute inset-0 opacity-20">
          <div className="w-full h-full" style={{
            backgroundImage: `
              linear-gradient(rgba(0, 255, 136, 0.4) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0, 255, 136, 0.4) 1px, transparent 1px)
            `,
            backgroundSize: '16px 16px'
          }} />
        </div>

        {/* Líneas de circuito ciberpunk */}
        <div className="absolute inset-0 opacity-30">
          {/* Líneas horizontales */}
          <div className="absolute top-2 left-0 w-full h-0.5" style={{
            background: 'linear-gradient(90deg, transparent, #00ffff, #00ff88, transparent)',
            animation: 'cyberpunkGlow 3s ease-in-out infinite alternate'
          }} />
          <div className="absolute bottom-6 left-0 w-full h-0.5" style={{
            background: 'linear-gradient(90deg, transparent, #ff00ff, #00ff88, transparent)',
            animation: 'cyberpunkGlow 4s ease-in-out infinite alternate-reverse'
          }} />
          
          {/* Líneas verticales */}
          <div className="absolute top-0 left-4 w-0.5 h-full" style={{
            background: 'linear-gradient(180deg, transparent, #00ffff, transparent)',
            animation: 'cyberpunkGlow 2.5s ease-in-out infinite alternate'
          }} />
          <div className="absolute top-0 right-6 w-0.5 h-full" style={{
            background: 'linear-gradient(180deg, transparent, #ff00ff, transparent)',
            animation: 'cyberpunkGlow 3.5s ease-in-out infinite alternate-reverse'
          }} />
        </div>

        {/* Puntos de conexión ciberpunk */}
        <div className="absolute inset-0 opacity-40">
          <div className="absolute top-2 left-4 w-2 h-2 rounded-full" style={{
            background: 'radial-gradient(circle, #00ffff, transparent)',
            animation: 'cyberpunkPulse 2s ease-in-out infinite'
          }} />
          <div className="absolute top-2 right-6 w-1.5 h-1.5 rounded-full" style={{
            background: 'radial-gradient(circle, #ff00ff, transparent)',
            animation: 'cyberpunkPulse 2.5s ease-in-out infinite alternate'
          }} />
          <div className="absolute bottom-6 left-8 w-1.5 h-1.5 rounded-full" style={{
            background: 'radial-gradient(circle, #00ff88, transparent)',
            animation: 'cyberpunkPulse 3s ease-in-out infinite alternate-reverse'
          }} />
          <div className="absolute bottom-6 right-4 w-2 h-2 rounded-full" style={{
            background: 'radial-gradient(circle, #ffff00, transparent)',
            animation: 'cyberpunkPulse 1.8s ease-in-out infinite'
          }} />
        </div>

        {/* Efectos de scan lines - múltiples líneas visibles */}
        {isPlaying && (
          <>
            {/* Línea de escaneo 1 */}
            <div className="absolute inset-0 opacity-25" style={{
              background: 'linear-gradient(180deg, transparent 0%, transparent 48%, rgba(0, 255, 136, 0.8) 50%, transparent 52%, transparent 100%)',
              animation: 'cyberpunkScanContinuous1 4s linear infinite'
            }} />
            
            {/* Línea de escaneo 2 */}
            <div className="absolute inset-0 opacity-20" style={{
              background: 'linear-gradient(180deg, transparent 0%, transparent 23%, rgba(0, 255, 255, 0.6) 25%, transparent 27%, transparent 100%)',
              animation: 'cyberpunkScanContinuous2 5s linear infinite'
            }} />
            
            {/* Línea de escaneo 3 */}
            <div className="absolute inset-0 opacity-15" style={{
              background: 'linear-gradient(180deg, transparent 0%, transparent 73%, rgba(255, 0, 255, 0.4) 75%, transparent 77%, transparent 100%)',
              animation: 'cyberpunkScanContinuous3 6s linear infinite'
            }} />
            
            {/* Patrón de líneas estáticas adicional */}
            <div className="absolute inset-0 opacity-10" style={{
              background: 'repeating-linear-gradient(0deg, transparent, transparent 8px, rgba(0, 255, 136, 0.2) 8px, rgba(0, 255, 136, 0.2) 10px)'
            }} />
          </>
        )}

        {/* Efectos de interferencia */}
        <div className="absolute inset-0 opacity-10" style={{
          background: 'linear-gradient(45deg, transparent 49%, rgba(0, 255, 255, 0.1) 50%, transparent 51%)',
          animation: 'cyberpunkInterference 6s ease-in-out infinite'
        }} />
        
        <div className="flex items-center justify-center gap-1 relative z-10 w-full px-2">
          {[...Array(24)].map((_, i) => {
            const barType = i % 8;
            const speedVariation = 0.8 + (i % 5) * 0.15;
            
            return (
              <div
                key={i}
                className="gaming-wave-bar flex-1"
                style={{
                  minWidth: '2px',
                  maxWidth: '8px',
                  height: '12px',
                  borderRadius: '1px',
                  background: isPlaying 
                    ? `linear-gradient(to top, 
                        #00ff88, 
                        #00cc6a, 
                        #33ff99,
                        #66ffaa,
                        #99ffcc
                      )` 
                    : `linear-gradient(to top, 
                        #1a3326, 
                        #2d4a3a, 
                        #40614e,
                        #537862,
                        #668f76
                      )`,
                  transformOrigin: 'center',
                  boxShadow: isPlaying
                    ? `
                      0 0 8px rgba(0, 255, 136, 0.8),
                      0 0 16px rgba(0, 255, 136, 0.6),
                      0 0 24px rgba(0, 255, 136, 0.4),
                      inset 0 1px 2px rgba(255, 255, 255, 0.3)
                    `
                    : `
                      0 0 3px rgba(0, 255, 136, 0.3),
                      0 0 6px rgba(0, 255, 136, 0.2),
                      inset 0 1px 1px rgba(255, 255, 255, 0.1)
                    `,
                  transition: 'all 0.8s ease-out',
                  animation: isPlaying 
                    ? `realisticWave${barType} ${speedVariation}s ease-in-out infinite`
                    : `subtleIdle${barType} ${3 + (i % 4)}s ease-in-out infinite, subtleGlow${barType} ${3 + (i % 4)}s ease-in-out infinite`
                }}
              />
            );
          })}
        </div>

        {/* Esquinas con efecto gaming */}
        <div className="absolute top-1 left-1 w-3 h-3 border-l-2 border-t-2 border-green-400 opacity-60" />
        <div className="absolute top-1 right-1 w-3 h-3 border-r-2 border-t-2 border-green-400 opacity-60" />
        <div className="absolute bottom-1 left-1 w-3 h-3 border-l-2 border-b-2 border-green-400 opacity-60" />
        <div className="absolute bottom-1 right-1 w-3 h-3 border-r-2 border-b-2 border-green-400 opacity-60" />
      </div>
    </div>
  );
};

export default AudioVisualizer;
